import { Link, NavLink, useLocation } from "react-router-dom";
import { ProfileNavLink } from "@/components/app/profile/ProfileNavLink";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LogIn, ChevronDown, ChevronRight, Sparkles, Calculator, Layers, GitCompare, Shield, School, MapPin, Wallet, Bookmark, Check, Trophy, Menu, X, Briefcase, Handshake, Send, UserCircle, Lightbulb } from "lucide-react";
import { useCmsRoles } from "@/hooks/useCmsRoles";
import { useEffect, useState } from "react";
import { bustCachePrefix } from "@/lib/useCachedFetch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GlobalSearchPanel, GlobalSearchBox } from "@/components/app/GlobalSearch";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";

export default function Navbar() {
  const { user, login, logout } = useAuth();
  const { currency, setCurrency, supported } = useCurrency();
  const { pathname } = useLocation();
  // On Plus-marketing surfaces (`/plus`, `/wua-plus`) the giant hero
  // Plus lockup sits ~200px below the navbar. Two versions of the same
  // brand mark side-by-side reads as visual competition, so we soften
  // the main navbar logo — still clickable (home-anchor), just receded.
  const isPlusSurface = pathname === "/plus" || pathname.startsWith("/wua-plus");

  // Count of local `wua:` cache entries — shown in the user menu next to a
  // "Clear cache" affordance. Refreshed when the menu opens.
  const [cacheCount, setCacheCount] = useState(0);
  const recountCache = () => {
    try {
      let n = 0;
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith("wua:")) n++;
      }
      setCacheCount(n);
    } catch { setCacheCount(0); }
  };
  useEffect(recountCache, [user]);

  const clearCache = () => {
    bustCachePrefix("wua:");
    recountCache();
    toast.success("Local cache cleared — next page load will refetch fresh data.");
  };

  // Mobile drawer state — closes when the user picks any nav link.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  // Report count badge in the user dropdown — refreshed on sign-in
  // and whenever a fresh order lands on /wua-plus/success (that page
  // sets a `wua:reports-dirty` flag on completion; we key off pathname
  // so returning to the app from the checkout redirect will refresh).
  const [reportsCount, setReportsCount] = useState(0);
  useEffect(() => {
    if (!user) { setReportsCount(0); return; }
    api.get("/wua-plus/reports")
      .then((r) => setReportsCount(r.data?.count || 0))
      .catch(() => setReportsCount(0));
  }, [user, pathname]);

  return (
    <header className="sticky top-0 z-50 bg-[#FDFBF7]/85 backdrop-blur border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        <Link
          to="/"
          data-testid="nav-home-link"
          className="flex items-center"
          aria-label="WhichUniversityAdvisor home"
        >
          <img
            src="/logo.png"
            alt="WhichUniversityAdvisor"
            width="220"
            height="88"
            style={{ height: "auto", width: "auto", maxWidth: "220px" }}
            className={`object-contain max-h-9 sm:max-h-11 transition-opacity duration-500 ${
              isPlusSurface
                ? "opacity-30 hover:opacity-70"
                : ""
            }`}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-5" aria-label="Main" data-testid="nav-desktop">
          {NAV.map((g) => <DesktopNavEntry key={g.key} entry={g} pathname={pathname} />)}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <GlobalSearchPanel />
          <ProfileNavLink className="hidden sm:inline-flex" />
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-menu-btn"
                aria-label="Open navigation menu"
                className="xl:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-stone-300 bg-white hover:bg-stone-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95]"
              >
                <Menu className="h-4 w-4 text-stone-700" strokeWidth={2} />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-full max-w-sm p-0 bg-[#FDFBF7] flex flex-col [&>button]:hidden"
              data-testid="nav-mobile-drawer"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
                <div className="text-[11px] font-underground-sc tracking-[0.18em] text-stone-500">Menu</div>
                <SheetClose asChild>
                  <button
                    data-testid="nav-mobile-close-btn"
                    aria-label="Close menu"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95]"
                  >
                    <X className="h-4 w-4 text-stone-500" strokeWidth={2} />
                  </button>
                </SheetClose>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
                <GlobalSearchBox idPrefix="drawer" onDone={() => setMobileOpen(false)} />
                <ProfileNavLink testId="nav-mobile-my-profile" onNavigate={() => setMobileOpen(false)} className="w-full items-start" />

                {NAV.map((g) => g.items ? (
                  <MobileNavSection key={g.key} title={g.label} testId={`nav-mobile-section-${g.key}`}>
                    {g.items.map((it) => <MobileNavLink key={it.to} to={it.to} label={it.label} testId={it.mobileTestId} />)}
                  </MobileNavSection>
                ) : (
                  <MobileNavLink key={g.key} to={g.to} label={g.label} testId={`nav-mobile-${g.key}`} accent={g.accent} standalone />
                ))}

                {user ? (
                  <MobileNavSection title={displayName(user)} testId="nav-mobile-section-account" wrapTitle>
                    {accountItems(reportsCount).map((it) => <MobileNavLink key={it.to} to={it.to} label={it.label} testId={`nav-mobile-account-${it.to.slice(1).replace(/\//g, "-")}`} />)}
                    <MobileManageLinks pathname={pathname} />
                    <button
                      type="button"
                      onClick={() => { setMobileOpen(false); logout(); }}
                      data-testid="nav-mobile-logout-btn"
                      className="mt-1 text-left px-1 py-3 text-[14px] font-underground font-semibold text-stone-600 hover:text-stone-900"
                    >
                      Log out
                    </button>
                  </MobileNavSection>
                ) : (
                  <Button
                    data-testid="nav-mobile-signin-btn"
                    onClick={() => { setMobileOpen(false); login(); }}
                    className="w-full h-11 bg-[#002147] text-white hover:bg-[#001A38] rounded-md"
                  >
                    <LogIn className="h-4 w-4 mr-1.5" /> Sign in
                  </Button>
                )}

                <div>
                  <div className="text-[11px] font-underground-sc tracking-[0.15em] text-stone-500 mb-2">Currency</div>
                  <div className="flex flex-wrap gap-1.5">
                    {supported.slice(0, 8).map((c) => (
                      <button
                        key={c}
                        data-testid={`nav-mobile-currency-${c}`}
                        onClick={() => setCurrency(c)}
                        className={`text-[12px] font-underground font-semibold min-h-[36px] px-3 py-1 rounded-md border transition-colors ${
                          currency === c
                            ? "border-stone-900 bg-stone-900 text-white"
                            : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="currency-selector-btn" className="font-underground font-semibold hidden xl:inline-flex">
                {currency} <ChevronDown className="ml-1 h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-72 overflow-auto">
              <DropdownMenuLabel>Display currency</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {supported.map((c) => (
                <DropdownMenuItem
                  key={c}
                  data-testid={`currency-option-${c}`}
                  onClick={() => setCurrency(c)}
                  className="font-underground font-semibold"
                >
                  {c}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu onOpenChange={(o) => o && recountCache()}>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-btn"
                  aria-label={`Account menu for ${displayName(user)}`}
                  className="flex items-center gap-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95]"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.picture} alt="" />
                    <AvatarFallback>{displayName(user)[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <span className="hidden xl:inline max-w-[10rem] truncate text-[13px] font-underground font-semibold text-stone-800" data-testid="user-menu-name" title={displayName(user)}>
                    {displayName(user)}
                  </span>
                  <ChevronDown className="hidden xl:inline h-3 w-3 text-stone-500" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 max-w-[calc(100vw-1rem)]">
                <DropdownMenuLabel className="font-editorial break-words" data-testid="user-menu-display-name">{displayName(user)}</DropdownMenuLabel>
                {user.email && <div className="px-2 pb-2 text-xs text-stone-500 font-mono break-all">{user.email}</div>}
                <DropdownMenuSeparator />
                {accountItems(reportsCount).map((it) => <AccountMenuItem key={it.to} item={it} />)}
                <UserMenuManageSection pathname={pathname} />
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={clearCache}
                  data-testid="clear-cache-btn"
                  disabled={cacheCount === 0}
                  className="flex items-center justify-between gap-4"
                >
                  <span>Clear local cache</span>
                  <span className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                    {cacheCount === 0 ? "empty" : `${cacheCount} ${cacheCount === 1 ? "entry" : "entries"}`}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              data-testid="signin-btn"
              onClick={login}
              className="bg-[#002147] text-white hover:bg-[#001A38] rounded-md h-10 px-3"
              size="sm"
            >
              <LogIn className="hidden sm:inline h-4 w-4 mr-1.5" aria-hidden />
              Sign in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}


// -------------------- Navigation model --------------------
// One ordered model drives both the desktop dropdowns and the mobile drawer.
const NAV = [
  { key: "universities", label: "Universities", section: "Universities", match: ["/explore", "/uni"], items: [
    { to: "/explore", label: "Explore", hint: "Browse and filter every university we cover", testId: "nav-explore-link", mobileTestId: "nav-mobile-explore", Icon: School, accent: "text-[#002147] bg-[#E8EEF6]" },
  ] },
  { key: "courses", label: "Courses", section: "Courses", items: [
    { to: "/admission", label: "Quick Check", hint: "Free chance estimate", testId: "nav-admission-link", mobileTestId: "nav-mobile-admission", Icon: Sparkles, accent: "text-emerald-600 bg-emerald-50" },
    { to: "/beta/course-fit", label: "Fast Match", hint: "Your course interests & how you like to study · ~3 min", testId: "nav-fast-match-link", mobileTestId: "nav-mobile-fast-match", Icon: Sparkles, accent: "text-[#4C1D95] bg-[#F5F0FF]" },
    { to: "/advanced", label: "Advanced", hint: "6-layer matching engine", testId: "nav-advanced-link", mobileTestId: "nav-mobile-advanced", Icon: Layers, accent: "text-[#002147] bg-[#E8EEF6]" },
    { to: "/rankings", label: "Subject Rankings", hint: "Subject × country rankings on graduate outcomes", testId: "nav-rankings-link", mobileTestId: "nav-mobile-rankings", Icon: Trophy, accent: "text-amber-700 bg-amber-50" },
    { to: "/highest-earning", label: "UK’s Highest Earnings", hint: "UK courses ranked by median graduate salary", testId: "nav-highest-earning-link", mobileTestId: "nav-mobile-highest-earning", Icon: Wallet, accent: "text-emerald-600 bg-emerald-50" },
  ] },
  { key: "firms", label: "Firms", section: "Employers", match: ["/how-to-get-into", "/get-me-to"], items: [
    { to: "/firm-guides", label: "Firm Guides", hint: "How to get into top employers", testId: "nav-firm-guides-link", mobileTestId: "nav-mobile-firm-guides", Icon: Trophy, accent: "text-emerald-600 bg-emerald-50" },
    { to: "/events", label: "Firm Events", hint: "Insight weeks, spring weeks & open days", testId: "nav-events-link", mobileTestId: "nav-mobile-events", Icon: Briefcase, accent: "text-[#4C1D95] bg-[#F5F0FF]" },
  ] },
  { key: "cities", label: "Cities", section: "Student city intelligence", match: ["/cities"], items: [
    { to: "/cities", label: "All City Guides", hint: "Editorial student city guides worldwide", testId: "nav-cities-index-link", mobileTestId: "nav-mobile-cities", Icon: MapPin, accent: "text-[#002147] bg-[#E8EEF6]" },
    { to: "/cities/matcher", label: "City Matcher", hint: "Find your city on your budget", testId: "nav-cities-matcher-link", mobileTestId: "nav-mobile-cities-matcher", Icon: Wallet, accent: "text-[#7C3AED] bg-[#F5F0FF]" },
    { to: "/cities/compare", label: "Compare Cities", hint: "Side-by-side cost & vibe", testId: "nav-cities-compare-link", mobileTestId: "nav-mobile-cities-compare", Icon: GitCompare, accent: "text-emerald-600 bg-emerald-50" },
    { to: "/cities/hubs", label: "Industry Hubs", hint: "Top cities per sector — law, tech, finance, biotech…", testId: "nav-cities-hubs-link", mobileTestId: "nav-mobile-cities-hubs", Icon: Trophy, accent: "text-amber-700 bg-amber-50" },
  ] },
  { key: "guides", label: "Guides", to: "/guides" },
  { key: "tools", label: "Tools", section: "Tools", items: [
    { to: "/grade-calculator", label: "Grade Calc", hint: "Convert grades → UCAS", testId: "nav-grade-calc-link", mobileTestId: "nav-mobile-grade-calc", Icon: Calculator, accent: "text-sky-600 bg-sky-50" },
    { to: "/compare", label: "Compare", hint: "Side-by-side degrees", testId: "nav-compare-link", mobileTestId: "nav-mobile-compare", Icon: GitCompare, accent: "text-amber-600 bg-amber-50" },
  ] },
  { key: "counsellors", label: "Counsellors", to: "/counsellors" },
  { key: "plus", label: "WUA+", to: "/plus", accent: "text-[#4C1D95]" },
];

const _ACCOUNT_ITEMS = [
  { to: "/profile", label: "My Profile", hint: "Home country, grades, budget, nationality", testId: "user-menu-profile", Icon: UserCircle, accent: "bg-[#F5F0FF] text-[#4C1D95]" },
  { to: "/me/talent-profile", label: "Talent Profile", hint: "Let Verified Partner firms find you", testId: "user-menu-talent-profile", Icon: Handshake, accent: "bg-[#F5F0FF] text-[#4C1D95]" },
  { to: "/applications", label: "My Applications", hint: "Checklists + one-tap UCAS / Common App exports", testId: "user-menu-applications", Icon: Send, accent: "bg-emerald-50 text-emerald-700" },
  { to: "/realisations", label: "My Realisations", hint: "What you've learned about yourself", testId: "user-menu-realisations", Icon: Lightbulb, accent: "bg-[#F5F0FF] text-[#4C1D95]" },
  { to: "/shortlist", label: "My Shortlist", hint: "Courses you've bookmarked", testId: "user-menu-shortlist", Icon: Check, accent: "bg-emerald-50 text-emerald-700" },
  { to: "/saved", label: "Saved Searches", hint: "Revisit any Quick Check you stashed", testId: "user-menu-saved-searches", Icon: Bookmark, accent: "bg-[#F5F0FF] text-[#4C1D95]" },
];

// Purchased WUA+ reports stay reachable, but only for people who have some.
function accountItems(reportsCount) {
  if (!reportsCount) return _ACCOUNT_ITEMS;
  return [..._ACCOUNT_ITEMS, { to: "/plus/my-reports", label: "My reports", hint: `${reportsCount} Personalised ${reportsCount === 1 ? "Report" : "Reports"}`, testId: "user-menu-my-reports", Icon: Sparkles, accent: "bg-[#F5F0FF] text-[#4C1D95]" }];
}

export function displayName(user) {
  return (user?.name || "").trim() || (user?.email || "").split("@")[0] || "Your account";
}

const _isOn = (pathname, to) => pathname === to || pathname.startsWith(to + "/");

function DesktopNavEntry({ entry, pathname }) {
  if (!entry.items) {
    return (
      <NavLink
        to={entry.to}
        data-testid={`nav-${entry.key}-link`}
        className={({ isActive }) =>
          `text-[13px] font-underground font-semibold tracking-wide whitespace-nowrap rounded py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95] focus-visible:ring-offset-2 ${
            entry.accent ? `${entry.accent} hover:text-[#3B0764]` : isActive ? "text-stone-900" : "text-stone-500 hover:text-stone-900"
          }`
        }
      >
        {entry.label}
      </NavLink>
    );
  }
  const active = entry.items.some((t) => _isOn(pathname, t.to)) || (entry.match || []).some((m) => _isOn(pathname, m));
  return (
    <RichDropdown
      triggerTestId={`nav-${entry.key}-btn`}
      triggerLabel={entry.label}
      triggerActive={active}
      triggerActiveClass="text-stone-900 font-semibold hover:text-stone-900"
      triggerBaseClass="text-stone-500 hover:text-stone-900"
      sectionLabel={entry.section}
      sectionLabelClass="text-stone-400"
      items={entry.items}
      hoverClass="bg-stone-50"
      itemTestidPrefix={`nav-${entry.key}-item`}
    />
  );
}

function AccountMenuItem({ item }) {
  return (
    <DropdownMenuItem asChild data-testid={item.testId} className="!p-0 focus:bg-stone-50">
      <Link to={item.to} className="group w-full flex items-center gap-3 px-2.5 py-2 cursor-pointer text-left hover:bg-stone-50 rounded-md">
        <span className={`shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${item.accent}`}>
          <item.Icon className="h-4 w-4" strokeWidth={2} aria-hidden />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-stone-900 leading-tight">{item.label}</div>
          <div className="text-[11px] text-stone-500 leading-snug mt-0.5">{item.hint}</div>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-stone-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" strokeWidth={2.5} aria-hidden />
      </Link>
    </DropdownMenuItem>
  );
}

/**
 * Reusable rich-row dropdown menu.
 * - Card-style rows with icon on left, title + hint centre, chevron on right
 * - Hover: chevron fades in + row bg tints in accent colour
 * - Left-aligned always; no shadcn `items-center` inheritance issues
 */
function RichDropdown({ triggerTestId, triggerLabel, triggerExtra, triggerActive, triggerActiveClass, triggerBaseClass, sectionLabel, sectionLabelClass, items, hoverClass, itemTestidPrefix }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={triggerTestId}
          className={`text-[13px] font-underground font-semibold tracking-wide whitespace-nowrap inline-flex items-center gap-1.5 rounded py-1 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95] focus-visible:ring-offset-2 ${
            triggerActive ? triggerActiveClass : triggerBaseClass
          }`}
        >
          {triggerLabel}
          {triggerExtra}
          <ChevronDown className="h-3 w-3 -ml-0.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={14}
        className="w-72 p-2 border-stone-200 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] rounded-xl bg-white opacity-100 data-[state=open]:opacity-100"
      >
        <div className={`px-2 pt-1 pb-2 text-[10px] font-underground-sc tracking-[0.14em] ${sectionLabelClass}`}>
          {sectionLabel}
        </div>
        <div className="flex flex-col gap-0.5">
          {items.map((t) => (
            <DropdownMenuItem
              asChild
              key={t.to}
              data-testid={t.testId || `${itemTestidPrefix}-${t.to.slice(1)}`}
              className={`rounded-lg !p-0 focus:${hoverClass}`}
            >
              <Link
                to={t.to}
                className={`group w-full flex items-center gap-3 px-2.5 py-2.5 cursor-pointer text-left transition-colors hover:${hoverClass}`}
              >
                <span className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center transition-transform group-hover:scale-105 ${t.accent}`}>
                  <t.Icon className="h-4 w-4" strokeWidth={2} aria-hidden />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-stone-900 leading-tight">{t.label}</div>
                  <div className="text-[11px] text-stone-500 leading-snug mt-0.5">{t.hint}</div>
                </div>
                <ChevronRight className="h-3.5 w-3.5 text-stone-300 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0" strokeWidth={2.5} />
              </Link>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// -------------------- Manage (CMS) links --------------------
// Only for platform admins and approved school / firm claim owners (same useCmsRoles guard as before).
function useManageItems(pathname) {
  const { isAdmin, schools, firms, loaded } = useCmsRoles();
  if (!loaded) return [];
  const items = [];
  if (isAdmin) {
    items.push({ to: "/admin", label: "Platform Admin", hint: "Moderation, claims, verification", testId: "user-menu-cms-admin", Icon: Shield, accent: "text-rose-600 bg-rose-50", active: pathname.startsWith("/admin") });
  }
  if (schools && schools.length > 0) {
    const first = schools[0];
    items.push({
      to: schools.length === 1 ? `/uni-portal/uni/${first.id}` : "/uni-portal",
      label: schools.length === 1 ? "Your School Portal" : `${schools.length} School Portals`,
      hint: schools.length === 1 ? `Edit ${first.name}` : `${schools.length} schools you manage`,
      testId: "user-menu-cms-portal", Icon: School, accent: "text-emerald-700 bg-emerald-50", active: pathname.startsWith("/uni-portal"),
    });
  }
  if (firms && firms.length > 0) {
    const first = firms[0];
    items.push({
      to: firms.length === 1 ? `/firm-portal/${first.slug}` : "/firm-portal",
      label: firms.length === 1 ? "Your Firm Portal" : `${firms.length} Firm Portals`,
      hint: firms.length === 1 ? `Edit ${first.name}` : `${firms.length} firms you manage`,
      testId: "user-menu-firm-portal", Icon: Briefcase, accent: "text-[#4C1D95] bg-[#F5F0FF]", active: pathname.startsWith("/firm-portal"),
    });
  }
  return items;
}

function UserMenuManageSection({ pathname }) {
  const items = useManageItems(pathname);
  if (!items.length) return null;
  return (
    <>
      <DropdownMenuSeparator />
      <div className="px-2 pt-2 pb-1 text-[10px] font-underground-sc tracking-[0.14em] text-stone-400">Manage</div>
      <div className="flex flex-col gap-0.5 pb-1">
        {items.map((it) => (
          <DropdownMenuItem asChild key={it.to} data-testid={it.testId} className="!p-0 focus:bg-stone-50">
            <Link to={it.to} className="group w-full flex items-center gap-3 px-2.5 py-2 cursor-pointer text-left hover:bg-stone-50 rounded-md">
              <span className={`shrink-0 h-8 w-8 rounded-lg flex items-center justify-center transition-transform group-hover:scale-105 ${it.accent}`}>
                <it.Icon className="h-4 w-4" strokeWidth={2} aria-hidden />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-stone-900 leading-tight flex items-center gap-1.5">
                  {it.label}
                  {it.active && <span className="text-[9px] font-underground-sc tracking-wider text-stone-400 font-normal">· here</span>}
                </div>
                <div className="text-[11px] text-stone-500 leading-snug mt-0.5 truncate">{it.hint}</div>
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-stone-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" strokeWidth={2.5} aria-hidden />
            </Link>
          </DropdownMenuItem>
        ))}
      </div>
    </>
  );
}

function MobileManageLinks({ pathname }) {
  const items = useManageItems(pathname);
  return items.map((it) => <MobileNavLink key={it.to} to={it.to} label={it.label} testId={`nav-mobile-${it.testId}`} />);
}

// -------------------- Mobile drawer helpers --------------------
function MobileNavSection({ title, testId, wrapTitle, children }) {
  return (
    <div data-testid={testId}>
      <div className={`text-[11px] font-underground-sc tracking-[0.15em] mb-1 text-stone-500 ${wrapTitle ? "break-words" : ""}`}>{title}</div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function MobileNavLink({ to, label, testId, accent, standalone }) {
  return (
    <NavLink
      to={to}
      end={to === "/cities"}
      data-testid={testId}
      className={({ isActive }) =>
        `flex min-h-[44px] items-center justify-between gap-3 px-1 py-2.5 border-b border-stone-100 font-underground font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95] ${
          standalone ? "text-[15px]" : "text-[14px]"
        } ${accent || (isActive ? "text-stone-900" : "text-stone-700 hover:text-stone-900")}`
      }
    >
      <span className="min-w-0 break-words">{label}</span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-stone-300" strokeWidth={2.5} aria-hidden />
    </NavLink>
  );
}
